import { CustomThemeOptions } from '../types';
import { ColorMode } from '../ThemeModeContext';
import { getGreentechThemeOptions, greentechThemeOptions } from './greentech';
import { getAutomniThemeOptions, automniThemeOptions } from './automni';
import { getCarsifThemeOptions, carsifThemeOptions } from './carsif';
import { getPaletransThemeOptions, paletransThemeOptions } from './paletrans';

export {
  getGreentechThemeOptions,
  greentechThemeOptions,
  getAutomniThemeOptions,
  automniThemeOptions,
  getCarsifThemeOptions,
  carsifThemeOptions,
  getPaletransThemeOptions,
  paletransThemeOptions,
};

type ThemeGetter = (mode: ColorMode) => CustomThemeOptions;

export const tenantThemeGetters: Record<string, ThemeGetter> = {
  // Override keys for local testing
  automni: getAutomniThemeOptions,
  greentech: getGreentechThemeOptions,
  carsif: getCarsifThemeOptions,
  paletrans: getPaletransThemeOptions,

  // Map hostnames to their respective theme options
  localhost: automniThemeOptions ? getAutomniThemeOptions : getAutomniThemeOptions,
  default: getAutomniThemeOptions,

  // Specific domains
  'telemetria.automni.com.br': getAutomniThemeOptions,
  'www.telemetria.automni.com.br': getAutomniThemeOptions,

  'greentech.com.br': getGreentechThemeOptions,
  'www.greentech.com.br': getGreentechThemeOptions,
  'greentech.automni.com.br': getGreentechThemeOptions,
  'www.greentech.automni.com.br': getGreentechThemeOptions,

  'telemetria.carsif.com.br': getCarsifThemeOptions,
  'www.telemetria.carsif.com.br': getCarsifThemeOptions,

  'paletrans.automni.com.br': getPaletransThemeOptions,
  'www.paletrans.automni.com.br': getPaletransThemeOptions,
};

// Backwards compatibility for existing imports
export const tenantThemes: Record<string, CustomThemeOptions> = {
  automni: automniThemeOptions,
  greentech: greentechThemeOptions,
  carsif: carsifThemeOptions,
  paletrans: paletransThemeOptions,
  localhost: automniThemeOptions,
  default: automniThemeOptions,
  'telemetria.automni.com.br': automniThemeOptions,
  'www.telemetria.automni.com.br': automniThemeOptions,
  'greentech.com.br': greentechThemeOptions,
  'www.greentech.com.br': greentechThemeOptions,
  'greentech.automni.com.br': greentechThemeOptions,
  'www.greentech.automni.com.br': greentechThemeOptions,
  'telemetria.carsif.com.br': carsifThemeOptions,
  'www.telemetria.carsif.com.br': carsifThemeOptions,
  'paletrans.automni.com.br': paletransThemeOptions,
  'www.paletrans.automni.com.br': paletransThemeOptions,
};

export const getThemeForHostname = (
  hostname: string,
  mode: ColorMode = 'light'
): CustomThemeOptions => {
  // Check URL param or localStorage override
  try {
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const urlTenant = params?.get('tenant_theme') || params?.get('tenant');
    if (urlTenant && tenantThemeGetters[urlTenant]) {
      localStorage.setItem('tenant_theme', urlTenant);
      return tenantThemeGetters[urlTenant](mode);
    }
    const override = localStorage.getItem('tenant_theme');
    if (override && tenantThemeGetters[override]) {
      return tenantThemeGetters[override](mode);
    }
  } catch (e) {
    // ignore
  }

  // Support exact hostname match
  if (tenantThemeGetters[hostname]) {
    return tenantThemeGetters[hostname](mode);
  }

  // Try to match part of the domain (e.g. if 'greentech' or 'carsif' is anywhere in the hostname)
  for (const key of Object.keys(tenantThemeGetters)) {
    if (hostname.includes(key)) {
      return tenantThemeGetters[key](mode);
    }
  }

  return tenantThemeGetters['default'](mode);
};
